import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink } from 'react-router-dom';
import { FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import './App.css';
import './animations.css';
import logo from './assets/logo.png';
import salonBg from './assets/salon-bg.mp4';
import AboutUs from './AboutUs';
import ContactUs from './ContactUs';
import Reels from './Reels';
import ServicesSection from './ServicesSection';
import MenuSection from './MenuSection';
import ImageGallery from './components/ImageGallery';
import Gallery from './components/Gallery';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import Blog from './Blog';
import BlogPost from './BlogPost';
import BlogsAdmin from './BlogsAdmin';
import Links from './components/Links';

// Redirect Component
const InvoiceRedirect = () => {
  const { pathname } = window.location;
  // Redirect /invoice/INV... -> /billing/#/invoice/INV...
  window.location.href = `/billing/#${pathname}`;
  return null;
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navbarRef = useRef(null);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const offset = window.scrollY;
          const isScrolled = offset > 50;

          if (isScrolled !== scrolled) {
            setScrolled(isScrolled);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Smooth scroll fallback for browsers that don't support CSS scroll-behavior
  useEffect(() => {
    const smoothScrollTo = (element, duration = 800) => {
      const targetPosition = element.offsetTop;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      let startTime = null;

      const animation = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = ease(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      };

      const ease = (t, b, c, d) => {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };

      requestAnimationFrame(animation);
    };

    // Apply smooth scroll to all anchor links (only for # anchors, not #/ routes)
    const links = document.querySelectorAll('a[href^="#"]');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && href.startsWith('#') && !href.startsWith('#/')) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            smoothScrollTo(target);
          }
        });
      }
    });

    return () => {
      links.forEach(link => {
        link.removeEventListener('click', () => { });
      });
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <Router>
      <div className="App">
        <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} ref={navbarRef}>
          <div className="nav-center">
            <ul className="nav-menu nav-left-menu desktop-menu">
              <li className="nav-item"><RouterLink to="/" className="nav-link" onClick={closeMobileMenu}>Home</RouterLink></li>
              <li className="nav-item"><RouterLink to="/about" className="nav-link" onClick={closeMobileMenu}>About Us</RouterLink></li>
            </ul>
            <div className="nav-logo-container visible">
              <div className="nav-logo">
                <img src={logo} alt="Celebrity Cuts Logo" />
              </div>
            </div>
            <ul className="nav-menu nav-right-menu desktop-menu">
              <li className="nav-item"><RouterLink to="/services" className="nav-link" onClick={closeMobileMenu}>Services</RouterLink></li>
              <li className="nav-item"><RouterLink to="/gallery" className="nav-link" onClick={closeMobileMenu}>Gallery</RouterLink></li>
              <li className="nav-item"><RouterLink to="/blog" className="nav-link" onClick={closeMobileMenu}>Blog</RouterLink></li>
            </ul>
            <div className="mobile-menu-icon" onClick={toggleMobileMenu}>
              <div className={`bar ${mobileMenuOpen ? 'open' : ''}`}></div>
              <div className={`bar ${mobileMenuOpen ? 'open' : ''}`}></div>
              <div className={`bar ${mobileMenuOpen ? 'open' : ''}`}></div>
            </div>
          </div>
          <div className="nav-right">
            <ul className="nav-menu right-menu desktop-menu">
              <li className="nav-item social-icons">
                <a href="https://www.instagram.com/celebritycutsinsta/" target="_blank" rel="noopener noreferrer" className="nav-social-link" aria-label="Instagram">
                  <FaInstagram className="nav-social-icon" />
                </a>
                <a href="https://www.facebook.com/people/celebrity-cuts/100087441225596/" target="_blank" rel="noopener noreferrer" className="nav-social-link" aria-label="Facebook">
                  <FaFacebookF className="nav-social-icon" />
                </a>
                <a href="https://www.youtube.com/@CelebrityCutsinvizag" target="_blank" rel="noopener noreferrer" className="nav-social-link" aria-label="YouTube">
                  <FaYoutube className="nav-social-icon" />
                </a>
              </li>
              <li className="nav-item contact-item">
                <RouterLink to="/contact" className="nav-link" onClick={closeMobileMenu}>Contact Us</RouterLink>
              </li>
            </ul>
          </div>
        </nav>
        {mobileMenuOpen && (
          <div className="mobile-menu small-menu">
            <ul className="mobile-nav-menu">
              <li className="nav-item"><RouterLink to="/" className="nav-link" onClick={closeMobileMenu}>Home</RouterLink></li>
              <li className="nav-item"><RouterLink to="/about" className="nav-link" onClick={closeMobileMenu}>About</RouterLink></li>
              <li className="nav-item"><RouterLink to="/services" className="nav-link" onClick={closeMobileMenu}>Services</RouterLink></li>
              <li className="nav-item"><RouterLink to="/gallery" className="nav-link" onClick={closeMobileMenu}>Gallery</RouterLink></li>
              <li className="nav-item"><RouterLink to="/blog" className="nav-link" onClick={closeMobileMenu}>Blog</RouterLink></li>
              <li className="nav-item"><RouterLink to="/contact" className="nav-link" onClick={closeMobileMenu}>Contact</RouterLink></li>
              <li className="nav-item contact-item"><RouterLink to="/book-now" className="nav-link" onClick={closeMobileMenu}>Book Now</RouterLink></li>
            </ul>
          </div>
        )}
        <Routes>
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/services" element={<ServicesSection showH1={true} />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/blogs" element={<BlogsAdmin />} />
          <Route path="/reels" element={<Reels />} />
          <Route path="/links" element={<Links />} />
          <Route path="/links" element={<Links />} />
          <Route path="/invoice/*" element={<InvoiceRedirect />} />
          <Route path="/" element={
            <>
              <header className="hero-section" id="home">
                <video className="background-video animate-fade-in" autoPlay loop muted playsInline>
                  <source src={salonBg} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="hero-content animate-fade-in-up delay-200">
                  <div className="hero-content-inner animate-stagger">
                    <div className="hero-logo-container">
                      <img
                        src={logo}
                        alt="Celebrity Cuts Logo"
                        className={`hero-logo ${scrolled ? 'hero-logo-scrolled' : ''} animate-fade-in`}
                      />
                      <p className="hero-tagline">Experience expert hair, skin, and beauty services at Celebrity Cuts –<br />where style meets professionalism.</p>
                    </div>
                  </div>
                </div>
              </header>
              <h1 className="hero-subtitle-h1">Unisex Salon in Vizag for Modern Styling</h1>
              <ImageGallery />
              <Reels />
              <section className="home-seo-section">
                <div className="container">
                  <h1 className="home-seo-h1">Experience expert hair, skin, and beauty services at Celebrity Cuts – where style meets professionalism.</h1>
                  <p className="home-seo-desc">Experience the pinnacle of hairdressing excellence</p>
                </div>
              </section>
              <ServicesSection />
              <MenuSection />
            </>
          } />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </div>
    </Router>
  );
}

export default App;
